import {
  ErrorResponse,
  GetNoteResp,
  isErrorResponse,
  Note,
  PostSearchData,
  PublicNotesResp,
} from "@types"

import customFetch, { APIError } from "./helper"
import { absoluteUrl } from "../utils/general"
import { Requests } from "@/types/ai"
import { AllUserInfoResponse } from "@/types/responses/public"
import { blogUpdatedTag, postSlugTag } from "@/lib/actions/revalidateTagParams"
import { BaseUserPublic } from "@/types/users"
import { notFound } from "next/navigation"
import { safeEncode } from "../utils"

const API = "/published_notes"

/**
 * Fetch listed, published notes. Fetches the fields for displaying the post card on the blog page
 * as well as extra fields specified by @includeFields.
 * Used when populating the blog page.
 */
export async function getPublishedNotes({
  blogSlug,
  limit = 10,
  offset = 0,
  includeFields,
  category,
}: {
  blogSlug: string
  limit: number
  offset: number
  includeFields?: string[]
  category?: string
}): Promise<Array<GetNoteResp>> {
  try {
    const query = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      includeFields: (includeFields || []).toString(),
    })

    if (category) {
      query.append("category", category)
    }

    const response = await customFetch<Array<GetNoteResp>>(
      API + `/new/blog/${decodeURIComponent(blogSlug)}?${query}`,
      {},
      {
        bypassCookies: true,
      }
    )

    return response
  } catch (error) {
    APIError.handle404Error(error)
  }
}

/**
 * Gets a count of all published notes a user has.
 *
 * This is used for pagination.
 */
export async function getPublishedNoteCount(
  blogSlug: string,
  category?: string
): Promise<number> {
  let url = API + `/new/blog/${decodeURIComponent(blogSlug)}/count`

  if (category) {
    const query = new URLSearchParams({
      category,
    })

    url += `?${query}`
  }

  const response = await customFetch<number>(
    url,
    {},
    {
      bypassCookies: true,
    }
  )
  return response
}

/**
 * Fetches the full data of a public post.
 * NOTE: If passing in post ID for @postSlug, you need to pass @doNotRedirectIdToSlug=true to not cause infinite refreshes
 *
 * @param blogSlug - The blog slug
 * @param postSlug - The post slug or post ID
 * @param redirectIdToSlug - (optional) If true, and Id for slug, redirect ID to slug
 */
export async function getPublicPost(
  blogSlug: string,
  postSlug: string,
  redirectIdToSlug?: boolean
): Promise<PublicNotesResp> {
  try {
    const response = await customFetch<PublicNotesResp | ErrorResponse>(
      `${API}/blog/${safeEncode(blogSlug)}/${safeEncode(postSlug)}${redirectIdToSlug ? "?redirectIdToSlug=true" : ""}`,
      {
        cache: "force-cache",
        next: {
          // TODO: Increase the specificity of this. Many people could have the same
          // post slugs so we should ideally use blogs too.
          tags: [postSlugTag(postSlug)],
        },
      },
      {
        ignoreImpersonation: true,
        bypassCookies: true,
      }
    )

    if (isErrorResponse(response)) {
      console.error("Failed to load post")
      throw response
    }

    // Special handling for the cast of zero posts: return a 404.
    if (response.notes.length === 0) notFound()

    return response
  } catch (error) {
    APIError.handle404Error(error)
  }
}

/**
 * Fetches the post preview.
 */
export async function getPostPreview(postId: string): Promise<Note> {
  try {
    const response = await customFetch<Note>(
      `${API}/preview/${postId}`,
      {
        cache: "no-cache",
      },
      {
        ignoreImpersonation: true,
        bypassCookies: true,
      }
    )

    return response
  } catch (error) {
    APIError.handle404Error(error)
  }
}

/**
 * Fetches a blog's most popular posts
 */
export async function getMostPopularPosts(
  blogSlug: string
): Promise<Array<Note>> {
  const response = await customFetch<Array<Note> | ErrorResponse>(
    `${API}/new/blog/${decodeURIComponent(blogSlug)}/popular`,
    {
      cache: "force-cache",
      next: {
        // Let's cache this for one day
        revalidate: 86400,
        // Let's tag this so we can revalidate it when the blog updates
        tags: [blogUpdatedTag(blogSlug)],
      },
    },
    {
      ignoreImpersonation: true,
      bypassCookies: true,
    }
  )

  if (isErrorResponse(response)) {
    console.error("Failed to load most popular posts")
    return []
  }

  return response
}

/**
 * This gets the base64 string placeholder for an image.
 *
 * It's cached forever in the Vercel data cache, so we can call this repeatedly
 * in server components and it'll return quickly in the cache if it's present.
 */
export async function getPlaceholder(imgUrl: string): Promise<string> {
  let fullyQualifiedUrl = imgUrl

  if (imgUrl.startsWith("/")) {
    fullyQualifiedUrl = absoluteUrl(imgUrl)
  }

  const response = await customFetch<string>(
    `${API}/placeholder/${encodeURIComponent(fullyQualifiedUrl)}`,
    {
      cache: "force-cache",
      next: {
        revalidate: false,
      },
    }
  )

  return response
}

export async function generateStreamedAiResponse(
  request: Requests
): Promise<ReadableStreamDefaultReader<string> | null> {
  /**
   * We need to use the globalThis fetch here because this is the only
   * API call that does NOT return a JSON response. So it needs special handling.
   */
  const response = await globalThis.fetch(`/api${API}/ai/generate/v2`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ request, stream: true }),
  })

  if (!response) return null

  if (!response || !response.body || response.status !== 200) return null
  return response.body.pipeThrough(new TextDecoderStream()).getReader()
}

export async function getSoundXyzEmbed(
  artist: string,
  release: string
): Promise<{ [key: string]: any } | undefined> {
  try {
    const response = await customFetch<{ [key: string]: any } | ErrorResponse>(
      `${API}/soundxyz/${artist}/${release}`
    )

    if (isErrorResponse(response)) {
      console.error("Unable to fetch Sound.xyz embed:", response.msg)
      return undefined
    }

    return response
  } catch (error) {
    console.error("Error fetching Sound.xyz embed:", error)
    return undefined
  }
}

export async function getTweet(
  id: string
): Promise<{ [key: string]: any } | undefined> {
  try {
    const response = await customFetch<
      { data: { [key: string]: any } } | ErrorResponse
    >(`${API}/tweet/${id}`)

    if (isErrorResponse(response)) {
      console.error("Unable to fetch tweet:", response.msg)
      return undefined
    }

    return response.data
  } catch (error) {
    console.error("Error fetching tweet:", error)
    return undefined
  }
}

export async function getEmbed(
  url: string
): Promise<{ [key: string]: any } | undefined> {
  try {
    const query = new URLSearchParams({ url }).toString()
    const response = await customFetch<
      { data: { [key: string]: any } } | ErrorResponse
    >(`${API}/embed?${query}`)

    if (isErrorResponse(response)) {
      console.error("Unable to fetch embed:", response.msg)
      return undefined
    }

    return response.data
  } catch (error) {
    console.error("Error fetching embed:", error)
    return undefined
  }
}

/**
 * Generate an AI image for a post
 * If @prompt isn't provided, defaults to generating a cover image based on the post contents
 */
export async function generateAiResponse(
  request: Requests
): Promise<any | null> {
  const res = await customFetch<{ res: any | null }>(`${API}/ai/generate/v2`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ request }),
  })

  return res
}

export async function getBlogAuthors(
  blogSlug: string
): Promise<BaseUserPublic[]> {
  const response = await customFetch<BaseUserPublic[] | ErrorResponse>(
    `${API}/new/blog/${blogSlug}/authors`,
    {
      cache: "force-cache",
      next: {
        // Let's cache this for one day
        revalidate: 86400,
        // Let's tag this so we can revalidate it when the blog authors update
        tags: [blogUpdatedTag(blogSlug)],
      },
    },
    {
      ignoreImpersonation: true,
      bypassCookies: true,
    }
  )

  if (isErrorResponse(response)) {
    console.error("Failed to load blog authors")
    return []
  }

  return response
}

async function getPublicUserInfoByUserId(
  userId: string,
  revalidate?: number
): Promise<AllUserInfoResponse> {
  const options: Record<string, any> = {}

  if (revalidate !== undefined) {
    options.next = { revalidate }
  }

  const response = await customFetch<AllUserInfoResponse>(
    `${API}/all-user-info/userid/${userId}`,
    options,
    {
      ignoreImpersonation: true,
      bypassCookies: true,
    }
  )

  return response
}

export async function getFeaturedPostByBlogSlug(
  blogSlug: string
): Promise<Note | null> {
  const response = await customFetch<Note | ErrorResponse>(
    `${API}/new/blog/${blogSlug}/featured`,
    {
      cache: "force-cache",
      next: {
        // Let's cache this for one day
        revalidate: 86400,
        // Let's tag this so we can revalidate it when the blog updates
        tags: [blogUpdatedTag(blogSlug)],
      },
    },
    {
      ignoreImpersonation: true,
      bypassCookies: true,
    }
  )

  if (isErrorResponse(response)) {
    console.error("Failed to load featured post")
    return null
  }

  return response
}

/**
 *  Get post search data for blog to facilitate frontend post search
 *  @param blogSlug - The blog to get the search data for
 *  @param publishedOnly - Only include published post data
 */
export async function getPostSearchDataByBlogSlug<T extends boolean>(
  blogSlug: string,
  publishedOnly?: T
): Promise<PostSearchData<T>[]> {
  const response = await customFetch<PostSearchData<T>[] | ErrorResponse>(
    `${API}/new/blog/${blogSlug}/search${publishedOnly ? "?published=true" : ""}`,
    {
      next: {
        // Let's tag this so we can revalidate it when the blog updates
        tags: [blogUpdatedTag(blogSlug)],
        revalidate: false,
      },
    },
    {
      ignoreImpersonation: true,
      bypassCookies: true,
    }
  )

  if (isErrorResponse(response)) {
    console.error("Failed to load featured post")
    return []
  }

  return response
}
